import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function GestionProduitsIntendant() {
    const [produits, setProduits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nom, setNom] = useState('');
    const [categorie, setCategorie] = useState('divers');
    const [unite, setUnite] = useState('');
    const [quantite, setQuantite] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [statistiques, setStatistiques] = useState({ consommation: [], total: 0 });
    const [periode, setPeriode] = useState('jour');

    // Utilisation de la configuration centralisée
    const API_BASE = config.apiBase;

    const fetchProduits = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/intendant/get_produits.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setProduits(data.produits);
            }
        } catch (error) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    const fetchStatistiques = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/intendant/get_statistiques.php?periode=${periode}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setStatistiques({ consommation: data.consommation, total: data.total });
            }
        } catch (error) {
            console.error('Erreur stats:', error);
        }
    };

    useEffect(() => {
        fetchProduits();
        fetchStatistiques();
    }, [periode]);

    const ajouterProduit = async () => {
        if (!nom.trim()) {
            toast.error('Nom du produit requis');
            return;
        }
        if (!quantite || parseInt(quantite) <= 0) {
            toast.error('Quantité valide requise');
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/intendant/ajouter_produit.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    nom: nom,
                    categorie: categorie,
                    unite: unite || 'unité',
                    quantite: parseInt(quantite)
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Produit ajouté');
                setNom('');
                setCategorie('divers');
                setUnite('');
                setQuantite('');
                fetchProduits();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setSubmitting(false);
        }
    };

    const approvisionner = async (id, stockActuel) => {
        const qte = prompt(`Stock actuel: ${stockActuel}\nQuantité à ajouter:`);
        if (!qte || parseInt(qte) <= 0) return;
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/intendant/approvisionner.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id_produit: id,
                    quantite: parseInt(qte)
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Stock mis à jour');
                fetchProduits();
                fetchStatistiques();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const categories = [
        { value: 'alimentaire', label: '🍚 Alimentaire' },
        { value: 'boisson', label: '🥤 Boisson' },
        { value: 'hygiene', label: '🧼 Hygiène' },
        { value: 'divers', label: '📦 Divers' }
    ];

    const maxConsommation = Math.max(...statistiques.consommation.map(c => c.total_consomme), 1);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="mx-auto max-w-6xl">
                {/* Header */}
                <div className="mb-8 rounded-2xl bg-gradient-to-r from-amber-600 to-orange-600 p-6 text-white shadow-xl">
                    <h1 className="text-2xl font-bold">🏪 Gestion du magasin</h1>
                    <p className="mt-2 opacity-90">Gérez les produits et suivez les consommations</p>
                </div>

                {/* Formulaire ajout produit */}
                <div className="mb-6 rounded-2xl bg-white p-6 shadow-xl">
                    <h2 className="text-lg font-bold text-slate-800 mb-4">➕ Nouveau produit</h2>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        <input
                            type="text"
                            placeholder="Nom du produit"
                            value={nom}
                            onChange={(e) => setNom(e.target.value)}
                            className="rounded-xl border border-slate-200 px-4 py-2 outline-none focus:border-amber-400"
                        />
                        <select
                            value={categorie}
                            onChange={(e) => setCategorie(e.target.value)}
                            className="rounded-xl border border-slate-200 px-4 py-2 outline-none"
                        >
                            {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                        <input
                            type="text"
                            placeholder="Unité (kg, L, pièce...)"
                            value={unite}
                            onChange={(e) => setUnite(e.target.value)}
                            className="rounded-xl border border-slate-200 px-4 py-2 outline-none"
                        />
                        <input
                            type="number"
                            placeholder="Quantité initiale"
                            value={quantite}
                            onChange={(e) => setQuantite(e.target.value)}
                            className="rounded-xl border border-slate-200 px-4 py-2 outline-none"
                        />
                        <button
                            onClick={ajouterProduit}
                            disabled={submitting}
                            className="bg-amber-600 text-white px-6 py-2 rounded-xl hover:bg-amber-700 disabled:opacity-50"
                        >
                            {submitting ? 'Ajout...' : 'Ajouter'}
                        </button>
                    </div>
                </div>

                {/* Statistiques de consommation */}
                <div className="mb-6 rounded-2xl bg-white p-6 shadow-xl">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-bold text-slate-800">📊 Consommation</h2>
                        <div className="flex gap-2">
                            <button onClick={() => setPeriode('jour')} className={`px-3 py-1 rounded-lg ${periode === 'jour' ? 'bg-amber-600 text-white' : 'bg-slate-100'}`}>Jour</button>
                            <button onClick={() => setPeriode('mois')} className={`px-3 py-1 rounded-lg ${periode === 'mois' ? 'bg-amber-600 text-white' : 'bg-slate-100'}`}>Mois</button>
                            <button onClick={() => setPeriode('annee')} className={`px-3 py-1 rounded-lg ${periode === 'annee' ? 'bg-amber-600 text-white' : 'bg-slate-100'}`}>Année</button>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <p className="text-center text-3xl font-bold text-amber-600">{statistiques.total}</p>
                            <p className="text-center text-sm text-slate-500">Unités consommées</p>
                        </div>
                        <div className="space-y-2">
                            {statistiques.consommation.slice(0, 5).map((p, idx) => (
                                <div key={idx}>
                                    <div className="flex justify-between text-sm">
                                        <span>{p.nom}</span>
                                        <span>{p.total_consomme}</span>
                                    </div>
                                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                        <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(p.total_consomme / maxConsommation) * 100}%` }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Liste des produits */}
                {loading ? (
                    <div className="flex justify-center py-12"><div className="h-12 w-12 animate-spin rounded-full border-4 border-amber-200 border-t-amber-600"></div></div>
                ) : produits.length === 0 ? (
                    <div className="rounded-2xl bg-white p-12 text-center"><p className="text-slate-500">Aucun produit disponible</p></div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {produits.map(p => (
                            <div key={p.id_produit} className="rounded-2xl bg-white p-5 shadow-lg">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-slate-800">{p.nom}</h3>
                                    {p.quantite <= p.seuil_alerte && p.quantite > 0 && <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">⚠️ Stock bas</span>}
                                    {p.quantite === 0 && <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">📦 Rupture</span>}
                                </div>
                                <p className="text-sm text-slate-500 mb-2">{categories.find(c => c.value === p.categorie)?.label || p.categorie}</p>
                                <div className="mb-3">
                                    <p className="text-3xl font-bold text-amber-600">{p.quantite}</p>
                                    <p className="text-sm text-slate-500">{p.unite}</p>
                                </div>
                                <button onClick={() => approvisionner(p.id_produit, p.quantite)} className="w-full bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700">
                                    📦 Approvisionner
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}